import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import {
    DateRangeFilter,
    formatCount,
    formatMoney,
    toAmount,
    usePagedRows,
} from "../../../../../shared/components/reports";
import { Pagination } from "../../../../../shared/ui/common/Pagination";
import { useCourseOverview, type CourseOverviewRow } from "../../../../reports";

interface Props {
    centerId: string;
    from: string;
    to: string;
    onFromChange: (day: string) => void;
    onToChange: (day: string) => void;
    onClear: () => void;
}

const HEADING = "py-2 pr-4 font-medium";
const CELL = "py-2.5 pr-4 text-content-primary";
const NUMBER = `${CELL} text-right tabular-nums`;

/**
 * A training centre's courses over the window, across their mentors: how
 * many mentorships, how they ended, how many students and mentors, what they
 * add up to — and the way into each course's own report.
 */
export default function CenterCourseReports({ centerId, from, to, onFromChange, onToChange, onClear }: Props) {
    const { t, i18n } = useTranslation();
    const locale = i18n.language;
    const { overview, loading, error, retry } = useCourseOverview(centerId, from, to);
    const rows = useMemo(() => overview?.courses ?? [], [overview]);
    const paged = usePagedRows(rows);
    const count = (value: number) => formatCount(value, locale);
    const link = (row: CourseOverviewRow) => ({
        pathname: `/center/reports/courses/${row.course_id}`,
        state: { label: row.name },
    });

    return (
        <>
            <section className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                <DateRangeFilter
                    idPrefix="course-reports"
                    from={from}
                    to={to}
                    onFromChange={onFromChange}
                    onToChange={onToChange}
                    onClear={onClear}
                />
            </section>

            <section className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                <h2 className="mb-4 text-base font-semibold text-content-primary">{t("reports.courses.title")}</h2>
                {loading && (
                    <p role="status" className="text-sm text-content-secondary">
                        {t("common.loading")}
                    </p>
                )}
                {error && (
                    <div role="alert" className="flex items-center justify-between gap-3 text-sm text-status-critical">
                        <span>{t("reports.error")}</span>
                        <button type="button" onClick={retry} className="font-medium underline">
                            {t("reports.retry")}
                        </button>
                    </div>
                )}
                {overview && rows.length === 0 && (
                    <p className="rounded-xl bg-surface-sunken p-6 text-center text-sm text-content-tertiary">
                        {t("reports.courses.empty")}
                    </p>
                )}
                {overview && rows.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[720px] text-left text-sm">
                            <thead>
                                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-content-tertiary dark:border-gray-800">
                                    <th scope="col" className={HEADING}>{t("reports.table.course")}</th>
                                    <th scope="col" className={`${HEADING} text-right`}>{t("reports.table.sessions")}</th>
                                    <th scope="col" className={`${HEADING} text-right`}>{t("reports.status.finished")}</th>
                                    <th scope="col" className={`${HEADING} text-right`}>{t("reports.status.pending")}</th>
                                    <th scope="col" className={`${HEADING} text-right`}>{t("reports.status.cancelled")}</th>
                                    <th scope="col" className={`${HEADING} text-right`}>{t("reports.courses.students")}</th>
                                    <th scope="col" className={`${HEADING} text-right`}>{t("reports.courses.mentors")}</th>
                                    {overview.has_amounts && (
                                        <th scope="col" className={`${HEADING} text-right`}>{t("reports.table.amount")}</th>
                                    )}
                                    <th scope="col" className={HEADING}>
                                        <span className="sr-only">{t("reports.center.viewReport")}</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {paged.page.map((row) => (
                                    <tr key={row.course_id} className="border-b border-gray-100 last:border-0 dark:border-gray-800">
                                        <th scope="row" className={`${CELL} font-medium`}>
                                            <Link to={link(row)} className="font-medium text-brand-600 underline-offset-2 hover:underline dark:text-brand-300">
                                                {row.name}
                                            </Link>
                                        </th>
                                        <td className={NUMBER}>{count(row.sessions)}</td>
                                        <td className={NUMBER}>{count(row.finished)}</td>
                                        <td className={NUMBER}>{count(row.pending)}</td>
                                        <td className={NUMBER}>{count(row.cancelled)}</td>
                                        <td className={NUMBER}>{count(row.students)}</td>
                                        <td className={NUMBER}>{count(row.mentors)}</td>
                                        {overview.has_amounts && (
                                            <td className={NUMBER}>
                                                {row.amount === null ? "—" : formatMoney(toAmount(row.amount), locale)}
                                            </td>
                                        )}
                                        <td className="py-1 text-right">
                                            <Link
                                                to={link(row)}
                                                className="inline-flex min-h-[44px] items-center rounded-lg px-3 text-sm font-medium text-brand-600 hover:bg-brand-50 dark:text-brand-300 dark:hover:bg-white/5"
                                            >
                                                {t("reports.center.viewReport")}
                                                <span className="sr-only">: {row.name}</span>
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                <Pagination
                    offset={paged.offset}
                    total={paged.total}
                    pageSize={paged.pageSize}
                    onPrev={paged.onPrev}
                    onNext={paged.onNext}
                />
            </section>
        </>
    );
}
